import { differenceInYears, fromUnixTime, format as dateFormat } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import en from 'date-fns/locale/en-US';
import cs from 'date-fns/locale/cs';
import { Locale } from 'date-fns/locale/types';
import { isWMF } from '@amf/shared/utils/wmf';

export function getAge(timestamp: number) {
  const date = fromUnixTime(timestamp);
  return differenceInYears(new Date(), date);
}

const formatRelativeCzechLocale = {
  lastWeek: "d.M.yyyy 'v' HH:mm",
  yesterday: "'Včera v' HH:mm",
  today: "'Dnes v' HH:mm",
  tomorrow: "'Zítra v' HH:mm",
  nextWeek: "d.M.yyyy 'v' HH:mm",
  other: "d.M.yyyy 'v' HH:mm",
};

const formatRelativeEnglishLocale = {
  lastWeek: 'd.M.yyyy HH:mm',
  yesterday: "'Yesterday at' HH:mm",
  today: "'Today at' HH:mm",
  tomorrow: "'Tomorrow at' HH:mm",
  nextWeek: 'd.M.yyyy HH:mm',
  other: 'd.M.yyyy HH:mm',
};

export function getDateLocale(locale: string): Locale {
  // @ts-ignore
  let dateLocale: Locale = {
    ...cs,
    formatRelative: token => formatRelativeCzechLocale[token],
  };
  if (locale === 'en') {
    // @ts-ignore
    dateLocale = {
      ...en,
      formatRelative: token => formatRelativeEnglishLocale[token],
    };
  }

  return dateLocale;
}

const mainTimeZone = process.env.NEXT_PUBLIC_EVENT_TIMEZONE ?? 'UTC';

type FormattedDates = {
  browser: string;
  main: string;
};

/**
 * Naformátuje UTC datum ve dvou časových pásmech.
 *
 * @param dateUTC - vstupní Date v UTC
 * @param formatStr - řetězec formátu (např. 'yyyy-MM-dd HH:mm')
 * @param locale - jazyk aplikace
 * @returns objekt s dvěma naformátovanými řetězci
 */
function format(
  dateUTC: Date,
  formatStr: string = 'dd/MM/yy HH:mm',
  locale?: string,
): FormattedDates {
  // Get date locale
  const dateLocale = getDateLocale(locale);

  if (!isWMF()) {
    return {
      browser: dateFormat(dateUTC, formatStr, { locale: dateLocale }),
      main: dateFormat(dateUTC, formatStr, { locale: dateLocale }),
    };
  }

  // Find browser timezone
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Formatted for browser timezone
  const browser = formatInTimeZone(dateUTC, browserTz, formatStr, {
    locale: dateLocale,
  });

  // Formatted for main event timezone
  const main = formatInTimeZone(dateUTC, mainTimeZone, formatStr, {
    locale: dateLocale,
  });

  return { browser, main };
}

function isBrowserHasSameTimeZoneOffset() {
  const date = new Date();
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const off1 = formatInTimeZone(date, browserTz, 'xxx');
  const off2 = formatInTimeZone(date, mainTimeZone, 'xxx');
  return off1 === off2;
}

const DATE = {
  format,
  isBrowserHasSameTimeZoneOffset,
};

export default DATE;
